/* Phase 16B -- AI Suite usage badge + budget-exceeded modal.
 *
 * Two exports:
 *   AiUsageBadge  -- small pill shown in the AI Suite header. Colour
 *                    bands per spec:
 *                        <80%  neutral
 *                        80-89 amber
 *                        90-99 red
 *                        100+  blocked (dashed border, disabled feel)
 *                    CEO role shows "unlimited" pill (ceo_bypass).
 *   BudgetExceededModal
 *                 -- fires when the backend returns 429 on execute. Two
 *                    CTAs: "Come back tomorrow" (dismiss) and "Request
 *                    more from Admin" (POST /ai/usage/request-more).
 *
 * Both listen on the global `ai-usage-refresh` custom event to refetch
 * without a full parent re-render. Fire it after every Ask / Do call.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, ShieldCheck, X, MailPlus, Clock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import errorToast from "@/lib/errorToast";

export function useAiUsage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const refetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/usage/me");
      setUsage(data);
    } catch {
      /* silent -- badge simply won't render */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
    const on = () => refetch();
    window.addEventListener("ai-usage-refresh", on);
    return () => window.removeEventListener("ai-usage-refresh", on);
  }, [refetch]);

  return { usage, loading, refetch };
}

function bandFor(pct) {
  if (pct >= 100) return "blocked";
  if (pct >= 90) return "red";
  if (pct >= 80) return "amber";
  return "ok";
}

export function AiUsageBadge({ usage }) {
  if (!usage) return null;
  if (usage.ceo_bypass) {
    return (
      <div
        data-testid="ai-usage-badge"
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
        title="CEO bypass -- unlimited AI messages"
      >
        <ShieldCheck className="h-3 w-3" />
        Unlimited
      </div>
    );
  }
  const band = bandFor(usage.pct_used || 0);
  const styles = {
    ok:      "bg-white/5 text-foreground/70 border-white/10",
    amber:   "bg-amber-500/15 text-amber-300 border-amber-400/30",
    red:     "bg-red-500/15 text-red-300 border-red-400/30",
    blocked: "bg-red-500/25 text-red-200 border-dashed border-red-400/50",
  }[band];
  return (
    <div
      data-testid="ai-usage-badge"
      data-band={band}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums border ${styles}`}
      title={`${usage.message_count} of ${usage.effective_cap} messages used today. Resets at ${usage.resets_at || "00:00 IST"}.`}
    >
      <Zap className="h-3 w-3" />
      <span data-testid="ai-usage-badge-count">
        {usage.message_count}/{usage.effective_cap}
      </span>
      <span className="opacity-60 hidden sm:inline">· resets {usage.resets_at || "00:00 IST"}</span>
    </div>
  );
}

export function BudgetExceededModal({ open, onClose, message, usage }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const requestMore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/usage/request-more", {
        reason: reason.trim() || null,
      });
      toast.success(
        `Request sent. ${data.approvers_notified || 0} admin(s) will review it.`,
      );
      onClose?.();
    } catch (e) {
      errorToast(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="ai-budget-exceeded-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0a1526]/95 border border-red-400/30 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-red-500/20 border border-red-400/40 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-red-300" />
                </div>
                <div>
                  <div className="font-heading text-sm uppercase tracking-[0.08em]">
                    Daily AI budget reached
                  </div>
                  <div className="text-[11px] text-foreground/60">
                    Resets at {usage?.resets_at || "00:00 IST"}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="ai-budget-modal-close"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white/5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <p className="text-sm text-foreground/85">
                {message ||
                  "You've used your allotted AI messages for the day. Come back after midnight IST, or request an extra 50 messages from your admin."}
              </p>
              {usage && (
                <div className="text-xs text-foreground/60 rounded-lg border border-white/10 bg-white/[0.03] p-3 tabular-nums">
                  <div className="flex justify-between">
                    <span>Used today</span>
                    <span data-testid="ai-budget-modal-used">
                      {usage.message_count}/{usage.effective_cap}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Bonus grants applied</span>
                    <span>{usage.bonus_grants || 0}</span>
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-foreground/55">
                  Optional -- tell your admin why (1 line)
                </label>
                <input
                  data-testid="ai-budget-request-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={280}
                  placeholder="Closing a deal / drafting a quote..."
                  className="mt-1 w-full text-sm bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 focus:border-sky-400/60 focus:outline-none"
                />
              </div>
            </div>

            <div className="p-4 pt-0 flex gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="ai-budget-dismiss"
                className="flex-1 text-foreground/70"
              >
                Come back tomorrow
              </Button>
              <Button
                onClick={requestMore}
                disabled={busy}
                data-testid="ai-budget-request-more"
                className="flex-1 bg-gradient-to-br from-sky-500 to-blue-700 text-white"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <MailPlus className="h-4 w-4 mr-1.5" />
                    Request more from Admin
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
